import { useMemo } from "react";
import { Building2, Users, CalendarDays, Shield, AlertTriangle, CheckCircle2, XCircle, Clock, ArrowRight } from "lucide-react";
import {
  Buildings as PhBuildings,
  Users as PhUsers,
  CalendarDots as PhCalendarDots,
  ShieldCheck as PhShieldCheck,
  CalendarBlank, ClockCounterClockwise, SealWarning,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { subMonths, format, parseISO } from "date-fns";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatCard } from "@/components/StatCard";
import { EmptyState } from "@/components/EmptyState";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { useDashboardStats } from "@/hooks/useDashboardStats";
import { useAuditLogs } from "@/hooks/useAuditLog";
import { useBookings } from "@/hooks/useBookings";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const activityIcons: Record<string, React.ElementType> = {
  success: CheckCircle2,
  error: XCircle,
  info: Clock,
  warning: AlertTriangle,
};
const activityColors: Record<string, string> = {
  success: "text-green-600",
  error:   "text-destructive",
  info:    "text-blue-500",
  warning: "text-amber-500",
};

function getLogType(action: string): string {
  if (action.includes("approved") || action.includes("collected") || action.includes("verified")) return "success";
  if (action.includes("blocked") || action.includes("rejected")) return "error";
  if (action.includes("expir") || action.includes("warning")) return "warning";
  return "info";
}

export default function Dashboard() {
  const { data: stats, isLoading } = useDashboardStats();
  const { data: logs = [] } = useAuditLogs();
  const { data: bookings = [] } = useBookings();
  const { assignments, activeOrgId } = useAuth();
  const navigate = useNavigate();
  const recentLogs = logs.slice(0, 5);

  const activeOrgName =
    assignments.find((a) => a.org_id === activeOrgId)?.organization?.name ?? "Your Building";

  // Booking trend — last 6 months
  const bookingTrend = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 6 }, (_, i) => {
      const d = subMonths(now, 5 - i);
      const month = d.getMonth();
      const year  = d.getFullYear();
      const count = (bookings as any[]).filter((b) => {
        try {
          const ci = parseISO(b.check_in);
          return ci.getMonth() === month && ci.getFullYear() === year;
        } catch { return false; }
      }).length;
      return { month: format(d, "MMM"), bookings: count };
    });
  }, [bookings]);

  const alertHosts = (stats?.hosts ?? []).filter((h: any) => {
    const percent = h.night_limit > 0 ? (h.nights_used / h.night_limit) * 100 : 0;
    return percent >= 75;
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground">
          {activeOrgName} — {stats?.totalUnits ?? 0} units, {stats?.activeHosts ?? 0} active STR hosts
        </p>
      </motion.div>

      {/* Stat cards — staggered entrance */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard delay={0}    title="Total Units"     value={stats?.totalUnits ?? 0}                            subtitle={`${stats?.strApproved ?? 0} STR approved`} icon={PhBuildings}     iconWeight="duotone" variant="info"    />
        <StatCard delay={0.08} title="Active Hosts"    value={stats?.activeHosts ?? 0}                                                                                icon={PhUsers}         iconWeight="duotone" variant="success" />
        <StatCard delay={0.16} title="Total Bookings"  value={stats?.totalBookings ?? 0}                                                                              icon={PhCalendarDots}  iconWeight="duotone" variant="default" />
        <StatCard delay={0.24} title="Compliance Rate" value={`${stats?.complianceRate ?? "100.0"}%`}            subtitle={`${stats?.blockedBookings ?? 0} blocked`}  icon={PhShieldCheck}   iconWeight="duotone" variant="success" />
      </div>

      {/* Booking activity trend chart */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.32, duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
      >
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm font-semibold">Booking Activity</CardTitle>
                <CardDescription className="text-xs">Check-ins by month — last 6 months</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {bookingTrend.every((d) => d.bookings === 0) ? (
              <EmptyState
                icon={CalendarBlank}
                title="No booking data yet"
                description="Bookings will appear here once check-ins are recorded."
              />
            ) : (
              <ResponsiveContainer width="100%" height={140}>
                <AreaChart data={bookingTrend} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                  <defs>
                    <linearGradient id="bookingGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="hsl(var(--primary))" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}    />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "6px",
                      fontSize: 12,
                      color: "hsl(var(--popover-foreground))",
                    }}
                    cursor={{ stroke: "hsl(var(--border))", strokeWidth: 1 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="bookings"
                    name="Bookings"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    fill="url(#bookingGrad)"
                    dot={false}
                    activeDot={{ r: 4, fill: "hsl(var(--primary))", strokeWidth: 0 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </motion.div>

      <motion.div
        className="grid grid-cols-1 gap-6 lg:grid-cols-3"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
      >
        {/* Recent activity */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">Recent Activity</CardTitle>
              <Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate("/audit-log")}>
                View all <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {recentLogs.length === 0 ? (
              <EmptyState icon={ClockCounterClockwise} title="No recent activity" description="Actions and events will appear here." />
            ) : (
              recentLogs.map((item: any, index: number) => {
                const type = getLogType(item.action);
                const Icon = activityIcons[type] || Clock;
                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.45 + index * 0.06, duration: 0.22 }}
                    className="flex items-start gap-3 rounded-md border border-border bg-card p-3"
                  >
                    <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${activityColors[type]}`} />
                    <div className="flex-1 min-w-0 space-y-0.5">
                      <p className="text-sm font-medium">{item.action}</p>
                      <p className="text-xs text-muted-foreground truncate">{item.detail || item.entity}</p>
                    </div>
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">
                      {new Date(item.created_at).toLocaleString()}
                    </span>
                  </motion.div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Alerts */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" /> Alerts
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {alertHosts.length === 0 ? (
              <EmptyState icon={SealWarning} title="No alerts" description="All hosts are within their limits." />
            ) : (
              alertHosts.map((h: any, index: number) => {
                const percent    = h.night_limit > 0 ? Math.round((h.nights_used / h.night_limit) * 100) : 0;
                const isCritical = percent >= 95;
                return (
                  <motion.div
                    key={h.id}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.45 + index * 0.07, duration: 0.22 }}
                    className={`rounded-md border p-3 ${isCritical ? "border-destructive/20 bg-destructive/5" : "border-amber-500/20 bg-amber-500/5"}`}
                  >
                    <p className={`text-xs font-semibold ${isCritical ? "text-destructive" : "text-amber-600"}`}>
                      Unit {(stats?.units ?? []).find((u: any) => u.id === h.unit_id)?.unit_number ?? "?"} — {isCritical ? "Night Limit" : "Approaching Limit"}
                    </p>
                    <p className="text-[11px] text-muted-foreground">{h.nights_used}/{h.night_limit} nights used. {h.night_limit - h.nights_used} remaining.</p>
                    <Progress value={percent} className="mt-2 h-1.5" />
                  </motion.div>
                );
              })
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
